import React from 'react';
import { Alert, Button, Container, Form, Spinner } from 'react-bootstrap';
import { Redirect, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import useHeader from 'containers/App/Header/useHeader';
import { useAdminLoginMutation } from 'services/api/admin';
import { storeAdminSession } from './adminSession';
import { AdminLoginPageStyles } from './style';

type AdminLoginPageProps = {
  onAuthenticated: () => void;
};

const getErrorMessage = (error: any) =>
  String(
    error?.data?.error ||
      error?.data?.message ||
      error?.error ||
      error?.message ||
      'Admin login failed.',
  );

function AdminLoginPage({ onAuthenticated }: AdminLoginPageProps) {
  useHeader('Admin Login');
  const location = useLocation<{ referrer?: string } | undefined>();
  const [adminLogin, { isLoading }] = useAdminLoginMutation();
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [errorMessage, setErrorMessage] = React.useState('');
  const [shouldRedirect, setShouldRedirect] = React.useState(false);

  const handleSubmit = async event => {
    event.preventDefault();
    setErrorMessage('');

    try {
      const response = await adminLogin({ username, password }).unwrap();

      if (!response.authenticated || !response.token) {
        throw new Error('Admin login failed.');
      }

      storeAdminSession({
        token: response.token,
        expiresAt: response.expiresAt,
      });
      toast.success('Admin access enabled.');
      onAuthenticated();
      setShouldRedirect(true);
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    }
  };

  if (shouldRedirect) {
    const referrer = location.state?.referrer;
    const requestedPath = `${location.pathname}${location.search}`;
    const destination =
      referrer ||
      (location.pathname !== '/admin' ? requestedPath : '/admin/monitoring');

    return <Redirect to={destination} />;
  }

  return (
    <AdminLoginPageStyles>
      <Container fluid className="admin-login-container">
        <div className="admin-login-panel">
          <section className="admin-login-intro">
            <div>
              <div className="admin-login-eyebrow">TV Labs Admin</div>
              <h1>Admin Center</h1>
              <p>
                Continue with the shared admin credentials for operational
                access.
              </p>
            </div>
            <div className="admin-login-session-note">
              TV Labs session active
            </div>
          </section>
          <section className="admin-login-form-section">
            <h2>Admin Login</h2>
            {errorMessage ? <Alert variant="danger">{errorMessage}</Alert> : null}
            <Form className="admin-login-form" onSubmit={handleSubmit}>
              <Form.Group
                className="admin-login-field admin-login-field-username"
                controlId="admin-username"
              >
                <Form.Label>Username</Form.Label>
                <Form.Control
                  autoComplete="username"
                  disabled={isLoading}
                  onChange={event => setUsername(event.target.value)}
                  required
                  type="text"
                  value={username}
                />
              </Form.Group>
              <div className="admin-login-field-spacer" aria-hidden="true" />
              <Form.Group
                className="admin-login-field admin-login-field-password"
                controlId="admin-password"
              >
                <Form.Label>Password</Form.Label>
                <Form.Control
                  autoComplete="current-password"
                  disabled={isLoading}
                  onChange={event => setPassword(event.target.value)}
                  required
                  type="password"
                  value={password}
                />
              </Form.Group>
              <div className="admin-login-actions">
                <Button
                  className="admin-login-submit"
                  disabled={isLoading}
                  type="submit"
                  variant="primary"
                >
                  {isLoading ? (
                    <>
                      <Spinner animation="border" size="sm" /> Signing in...
                    </>
                  ) : (
                    'Sign In'
                  )}
                </Button>
              </div>
            </Form>
          </section>
        </div>
      </Container>
    </AdminLoginPageStyles>
  );
}

export default AdminLoginPage;
